import Icon from './Icon';

/**
 * A radio or checkbox rendered as a clickable card or, at `density="row"`, as a compact row.
 *
 * Replaces six near-identical ~45-line blocks in DynamicForm: the primary-plan card, both
 * billing-period cards, the radio and checkbox question cards, the addon card, and the
 * one-time card.
 *
 * ACCESSIBILITY -- the important part:
 *
 * The pattern is a visually-hidden real input (`sr-only`) paired with a styled <label>. That
 * keeps the control genuinely keyboard-operable, but the original markup had NO focus styling
 * on the label, so a keyboard user tabbing through the plan options saw no indication of where
 * focus was. On a control choosing between $49 and $99/month that is a hard blocker.
 *
 * Fixed with Tailwind's `peer` variant: the input is marked `peer`, and the label carries
 * `peer-focus-visible:ring-2`. No JavaScript, and it works because the input already precedes
 * the label as a sibling. `focus-visible` (not `focus`) means the ring appears for keyboard
 * interaction but not on mouse click, so the visual design is unaffected.
 *
 * THE ROW DENSITY IS A PROP, NOT A COMPONENT. The redesign's answer rows are the same control
 * with tighter chrome: hairline separators instead of a border, no radius, 52px tall. Splitting
 * them into a second component would fork the focus-ring wiring above, and one of the two
 * copies would eventually lose it. That is the mistake this file was created to undo.
 *
 * @param {object} props
 * @param {'radio'|'checkbox'} props.type
 * @param {string} props.id            input id; the label's htmlFor
 * @param {string} props.name          input name (groups radios)
 * @param {boolean} props.checked
 * @param {Function} props.onChange
 * @param {string} props.title         primary line
 * @param {string} [props.subtitle]    secondary line under the title
 * @param {string} [props.priceLabel]  right-aligned price, e.g. "$99.00"
 * @param {string} [props.priceSuffix] small text under the price, e.g. "/mo"
 * @param {'card'|'row'} [props.density] 'card' (default) or the compact answer row
 * @param {React.ReactNode} [props.children] extra content inside the card
 */
export default function SelectableCard({
  type = 'radio',
  id,
  name,
  value,
  checked,
  onChange,
  title,
  subtitle,
  priceLabel,
  priceSuffix,
  density = 'card',
  children,
}) {
  const isRow = density === 'row';

  /*
    18px indicator in rows, 16px in cards, matching the mockups. Border is 1.5px rather than
    the card's 2px: at row scale a 2px ring reads as a filled dot even when unselected.
  */
  const indicatorSize = isRow ? 'w-[18px] h-[18px]' : 'w-4 h-4';
  const indicatorBorder = isRow ? 'border-1.5' : 'border-2';

  const indicator =
    type === 'radio' ? (
      // Filled dot for radios.
      <div
        className={`${indicatorSize} ${indicatorBorder} rounded-full flex items-center justify-center flex-shrink-0 ${
          checked ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
        }`}
      >
        {checked && <div className={`${isRow ? 'w-[7px] h-[7px]' : 'w-2 h-2'} bg-white rounded-full`} />}
      </div>
    ) : (
      // Checkmark for checkboxes.
      <div
        className={`${indicatorSize} ${indicatorBorder} rounded-sm flex items-center justify-center flex-shrink-0 ${
          checked ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
        }`}
      >
        {checked && <Icon name="checkSmall" className="w-3 h-3 text-white" />}
      </div>
    );

  /*
    A row's separator is drawn on its TOP edge, so a group of rows needs no knowledge of how
    many there are. The question card's header carries the matching `border-b`, which is how the
    mockups do it too: the first row's top border sits against the header's bottom border.

    Not `first:border-t-0`, which cannot work here. The `first:` variant compiles to
    `:first-child`, and the label is never its wrapper's first child -- the sr-only input is.
    `display: contents` on the wrapper does not help either, because it changes layout boxes
    and not the DOM tree that :first-child is resolved against.

    The focus ring is inset. A row is flush with its neighbours and the card edge, so an
    offset ring would be clipped by the card's border and vanish exactly when it is needed.
  */
  const labelClasses = isRow
    ? `flex w-full cursor-pointer items-center gap-2.5 border-t border-gray-100 px-3.5 py-3 min-h-[52px]
       border-l-[3px] transition-colors duration-150 sm:px-4
       peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-500
       ${
         checked
           ? // A 3px rail plus the tint. The rail is the only single-sided accent in the system,
             // and it is carried on EVERY row -- transparent when unselected -- because a border
             // that appears on selection would shift the row's text sideways as you pick.
             'border-l-primary-600 bg-primary-50'
           : 'border-l-transparent hover:bg-gray-50'
       }`
    : `block w-full p-4 border-2 rounded-sm cursor-pointer transition-all duration-200
       peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2
       ${
         checked
           ? 'border-primary-600 bg-primary-50'
           : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
       }`;

  /*
    An answer with no price renders NOTHING in this slot. There was briefly a "No charge" label
    here for the section-4 questions; on a row whose siblings are also unpriced it annotated every
    line with the same word and read as boilerplate.

    ONE LINE IN A ROW, stacked in a card.

    Stacking "$499.00" over "One-time" made the priced row visibly taller than its unpriced
    siblings, so a group of answers read as uneven when the only difference was that one of them
    costs money. Inline, every row is the same height and the amount still leads.
  */
  const priceNode =
    priceLabel &&
    (isRow ? (
      <span className="flex-shrink-0 text-sm font-semibold tabular-nums text-gray-900">
        {priceLabel}
        {priceSuffix && (
          /*
            HIDDEN BELOW `sm`, and that is the whole point of the inline treatment surviving on a
            phone. " one-time" is about 55px, which at 375px pushed "Yes, design one for me" onto
            a second line -- so the priced row grew to 65px against its sibling's 52px, which is
            the unevenness this change set out to remove. The amount is what the customer needs
            at that width; the term is restated in the order summary.
          */
          <span className="hidden font-normal text-gray-500 sm:inline">
            {' '}
            {priceSuffix.toLowerCase()}
          </span>
        )}
      </span>
    ) : (
      <div className="flex-shrink-0 text-right">
        <div className="font-semibold tabular-nums text-gray-900">{priceLabel}</div>
        {priceSuffix && <div className="text-disclaimer text-gray-500">{priceSuffix}</div>}
      </div>
    ));

  return (
    <div className="relative">
      <input
        type={type}
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
      />
      <label htmlFor={id} className={labelClasses}>
        {isRow ? (
          <>
            {indicator}
            <span className="min-w-0 flex-1">
              <span className={`block text-sm text-gray-900 ${checked ? 'font-semibold' : ''}`}>
                {title}
              </span>
              {subtitle && <span className="block text-sm text-gray-500">{subtitle}</span>}
            </span>
            {priceNode}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {indicator}
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">{title}</div>
                  {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
                </div>
              </div>

              {priceNode}
            </div>

            {children}
          </>
        )}
      </label>
    </div>
  );
}
